import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  LinearProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (currentPassword: string, newPassword: string) => void;
}

const getStrength = (pw: string) => {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { value: 25, label: "Weak", color: "#D32F2F" };
  if (score === 2) return { value: 50, label: "Fair", color: "#F57C00" };
  if (score === 3) return { value: 75, label: "Good", color: "#388E3C" };
  return { value: 100, label: "Strong", color: "#1B5E20" };
};

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose, onSave }) => {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const strength = getStrength(newPw);
  const match = newPw === confirm;
  const canSubmit = current.length > 0 && newPw.length >= 8 && match && confirm.length > 0;

  const handleClose = () => {
    setCurrent("");
    setNewPw("");
    setConfirm("");
    onClose();
  };

  const handleSave = () => {
    if (canSubmit) {
      onSave(current, newPw);
      handleClose();
    }
  };

  const EyeToggle = ({ show, onToggle }: { show: boolean; onToggle: () => void }) => (
    <InputAdornment position="end">
      <IconButton onClick={onToggle} edge="end" size="small">
        {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Current Password"
            type={showCurrent ? "text" : "password"}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              endAdornment: (
                <EyeToggle show={showCurrent} onToggle={() => setShowCurrent((p) => !p)} />
              ),
            }}
          />
          <TextField
            label="New Password"
            type={showNew ? "text" : "password"}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              endAdornment: <EyeToggle show={showNew} onToggle={() => setShowNew((p) => !p)} />,
            }}
          />
          {newPw.length > 0 && (
            <Box>
              <LinearProgress
                variant="determinate"
                value={strength.value}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#E0E0E0",
                  "& .MuiLinearProgress-bar": { backgroundColor: strength.color, borderRadius: 3 },
                }}
              />
              <Typography variant="caption" sx={{ color: strength.color }}>
                Strength: {strength.label}
              </Typography>
            </Box>
          )}
          <TextField
            label="Confirm New Password"
            type={showConfirm ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            size="small"
            error={confirm.length > 0 && !match}
            helperText={confirm.length > 0 && !match ? "Passwords do not match" : ""}
            InputProps={{
              endAdornment: (
                <EyeToggle show={showConfirm} onToggle={() => setShowConfirm((p) => !p)} />
              ),
            }}
          />
          <Box sx={{ backgroundColor: "rgba(23,139,142,0.05)", p: 1.5, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Password must include:
            </Typography>
            {[
              { rule: newPw.length >= 8, text: "At least 8 characters" },
              { rule: /[A-Z]/.test(newPw), text: "One uppercase letter" },
              { rule: /[0-9]/.test(newPw), text: "One number" },
              { rule: /[^A-Za-z0-9]/.test(newPw), text: "One special character" },
            ].map(({ rule, text }) => (
              <Typography
                key={text}
                variant="caption"
                display="block"
                sx={{ color: rule && newPw.length > 0 ? "#388E3C" : "text.secondary" }}
              >
                • {text}
              </Typography>
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSubmit}
          sx={{ textTransform: "none", backgroundColor: "#178B8E" }}
        >
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
